/**
 * PTEL Visor - LayerControl Component
 * Control de capas base para selección de proveedor de tiles
 *
 * VIS-06 - Accesibilidad: WCAG 2.1 (radiogroup, keyboard navigation)
 */

import React, { memo, useState, useCallback } from 'react';
import {
  TILE_CONFIGS,
  PROVIDERS_BY_PRIORITY,
  type TileProviderId,
} from '../../services/tiles';

export interface LayerControlProps {
  /** Proveedor activo */
  activeProvider: TileProviderId;
  /** Callback al cambiar proveedor */
  onProviderChange: (providerId: TileProviderId) => void;
  /** Estado inicial colapsado */
  defaultExpanded?: boolean;
  /** Clase CSS adicional */
  className?: string;
  /** Deshabilitar control */
  disabled?: boolean;
}

/** Iconos por proveedor */
const PROVIDER_ICONS: Record<TileProviderId, string> = {
  'ign-pnoa': '🛰️',
  'ign-base': '🗺️',
  'ign-mtn': '⛰️',
  'osm': '🌍',
};

export const LayerControl = memo<LayerControlProps>(function LayerControl({
  activeProvider,
  onProviderChange,
  defaultExpanded = false,
  className = '',
  disabled = false,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = useCallback(() => {
    if (!disabled) setExpanded((prev) => !prev);
  }, [disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, providerId: TileProviderId) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onProviderChange(providerId);
      }
    },
    [disabled, onProviderChange]
  );

  return (
    <div className={`layer-control ${className}`} data-testid="layer-control">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-controls="layer-control-options"
        disabled={disabled}
        className="layer-control__toggle"
        style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span aria-hidden="true">{expanded ? '▼' : '▶'}</span>
        <span>Capas</span>
      </button>

      {expanded && (
        <div
          id="layer-control-options"
          role="radiogroup"
          aria-label="Seleccionar capa base"
          style={{ margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: '4px' }}
        >
          {PROVIDERS_BY_PRIORITY.map((providerId) => {
            const config = TILE_CONFIGS[providerId];
            const isActive = providerId === activeProvider;
            return (
              <label
                key={providerId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                  padding: '4px',
                  borderRadius: '4px',
                  backgroundColor: isActive ? '#e0f2fe' : 'transparent',
                }}
                onKeyDown={(e) => handleKeyDown(e, providerId)}
              >
                <input
                  type="radio"
                  name="tile-provider"
                  value={providerId}
                  checked={isActive}
                  onChange={() => onProviderChange(providerId)}
                  disabled={disabled}
                  aria-describedby={`provider-desc-${providerId}`}
                />
                <span aria-hidden="true">{PROVIDER_ICONS[providerId]}</span>
                <span style={{ fontSize: '0.875rem' }}>{config.name}</span>
                {config.isOfficial && (
                  <span
                    style={{ fontSize: '0.625rem', color: '#059669', fontWeight: 600 }}
                    title="Proveedor oficial español"
                  >
                    ES
                  </span>
                )}
                <span id={`provider-desc-${providerId}`} className="sr-only">
                  {config.description}
                </span>
              </label>
            );
          })}
        </div>
      )}

      <style>{`
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `}</style>
    </div>
  );
});

export default LayerControl;
